import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AsyncValidatorFn, FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { IconComponent } from '../icons/icon-reusable';
import { emailIcon, eyeCloseIcon, eyeOpenIcon, IconDef } from '../icons/icon';
import { TextCaseMode, TextCaseReusableDirective } from '../text-case-reusable/text-case-reusable';
import { AutofillSyncDirective } from '../autofill-sync/autofill-sync.directive';
import { CustomValidators } from '../../utils/custom-validators';
import { registerApiFieldTarget } from '../../../service/api-field-errors.service';

type InputReusableType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'search'
  | 'tel'
  | 'url'
  | 'date'
  | 'time'
  | 'textarea';

type InputReusableSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-input-reusable',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconComponent,
    TextCaseReusableDirective,
    AutofillSyncDirective,
  ],
  templateUrl: './input-reusable.html',
  styles: [
    `
      input[type='password']::-ms-reveal,
      input[type='password']::-ms-clear {
        display: none;
      }
      /* Hide the native number spinner (up/down arrows) everywhere */
      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type='number'] {
        -moz-appearance: textfield;
        appearance: textfield;
      }
      /* Autofill detection hook for AutofillSyncDirective: Chrome/Safari fire an
         'animationstart' event the moment :-webkit-autofill is applied, letting
         us push the autofilled value into the reactive form. The animation is
         intentionally a no-op so nothing visibly changes. */
      @keyframes onAutoFillStart {
        from {
          opacity: 1;
        }
        to {
          opacity: 1;
        }
      }
      input:-webkit-autofill {
        animation-name: onAutoFillStart;
        animation-duration: 0.01s;
      }
    `,
  ],
})
export class InputReusable implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  ngOnDestroy(): void {
    if (this.disabledByInput && this.control.disabled) {
      this.control.enable({ emitEvent: false });
    }
  }

  private static nextUniqueId = 0;
  /** Lets a rejected save's field error find, mark and scroll to this field. */
  private readonly apiFieldTarget = registerApiFieldTarget(() => [this.control], () => this.label);

  @Input() type: InputReusableType = 'text';
  @Input() size: InputReusableSize = 'lg';
  @Input() label = '';
  @Input() labelClass = 'mb-1 block text-sm font-medium !text-slate900';
  @Input() placeholder = '';
  @Input() name = '';
  @Input() id = '';
  @Input() autocomplete = '';
  @Input() control: FormControl<string | number | null> = new FormControl<string | number | null>(
    '',
  );
  @Input() inputId = '';
  @Input() testId = '';
  @Input() required = false;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() textCase: TextCaseMode = 'none';
  @Input() preventPaste = false;
  @Input() preventCopy = false;
  @Input() suffixIcon: IconDef | null = null;
  @Input() externalErrorMessage = '';
  @Input() errorLabel = '';
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step: number | null = null;
  /**
   * Lets a `number` field accept a leading minus without having to declare a
   * negative `min`. Off by default, so existing non-negative fields are unaffected.
   */
  @Input() allowNegative = false;
  @Input() decimalPlaces = 2;
  @Input() pattern = '';
  @Input() enforcePasswordStrength = false;
  @Input() showCount = false;
@Input() maxCount: number | null = null;
@Input() rows: number = 4;
  /**
   * Textareas can be stretched vertically with the mouse by default. Set an inline style
   * rather than a `resize-*` utility so no stylesheet or layer can quietly switch it off.
   * Opt out with `[resizable]="false"`.
   */
  @Input() resizable = true;


  @ViewChild('suffixSlot') private suffixSlot?: ElementRef<HTMLElement>;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Currency fields always show their symbol on the left of the amount. Callers
   * supply the symbol in several shapes, all handled here so no page has to change:
   * - projected as `suffix-icon` (`<app-currency suffix-icon>`, a span wrapping
   *   `<app-currency>`, or a span of plain `&pound;`): the suffix slot moves left;
   * - an absolutely positioned overlay span placed right after this component
   *   (`<app-input-reusable class="currency-input"><span class="absolute right-3">£`):
   *   that span is pinned to the left.
   * Either way the input pads on the left to clear the symbol.
   */
  currencyOnLeft = false;

  showPassword = false;
  readonly emailIcon = emailIcon;
  readonly eyeOpenIcon = eyeOpenIcon;
  readonly eyeCloseIcon = eyeCloseIcon;
  readonly generatedId = `input-reusable-${InputReusable.nextUniqueId++}`;
  private baseValidator: ValidatorFn | null = null;
  private baseAsyncValidator: AsyncValidatorFn | null = null;
  private disabledByInput = false;

  ngOnInit(): void {
    this.captureBaseValidators();
    this.syncDisabledState();
    this.applyInputSpecificValidation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this.captureBaseValidators();
      this.applyInputSpecificValidation();
      this.syncDisabledState();
    }

    if (changes['disabled']) {
      this.syncDisabledState();
    }

    if (changes['type'] || changes['required'] || changes['enforcePasswordStrength']) {
      this.applyInputSpecificValidation();
      this.showPassword = false;
    }
  }

  get resolvedType(): string {
    if (this.type === 'textarea') {
      return 'text';
    }

    if (this.type === 'password') {
      return this.showPassword ? 'text' : 'password';
    }

    // A native number input reports an empty value while a lone "-" is being
    // typed, so the minus never survives. Negative-capable fields run as text
    // instead; handleInput still sanitises them down to a numeric value.
    if (this.type === 'number' && this.negativeAllowed) {
      return 'text';
    }

    return this.type;
  }

  ngAfterViewChecked(): void {
    const next = this.detectCurrencySuffix() || this.pinCurrencyOverlay();
    if (next !== this.currencyOnLeft) {
      this.currencyOnLeft = next;
      this.cdr.detectChanges();
    }
  }

  /** Finds a currency-symbol overlay span sitting right after the host and pins it left. */
  private pinCurrencyOverlay(): boolean {
    if (this.isTextArea) {
      return false;
    }
    const overlay = this.hostRef.nativeElement.nextElementSibling as HTMLElement | null;
    if (
      !overlay ||
      overlay.tagName !== 'SPAN' ||
      !overlay.classList.contains('absolute') ||
      !this.isCurrencySymbol(overlay)
    ) {
      return false;
    }
    if (overlay.style.left !== '0.75rem') {
      overlay.style.left = '0.75rem';
      overlay.style.right = 'auto';
    }
    return true;
  }

  private isCurrencySymbol(el: HTMLElement): boolean {
    return !!el.querySelector('app-currency') || /^[£$€]$/.test((el.textContent ?? '').trim());
  }

  private detectCurrencySuffix(): boolean {
    const slot = this.suffixSlot?.nativeElement;
    if (!slot || slot.querySelector('app-icon-reusable')) {
      return false;
    }
    return this.isCurrencySymbol(slot);
  }

  /** Keeps the numeric keypad on mobile for fields running as text. */
  get resolvedInputMode(): string | null {
    return this.type === 'number' ? 'decimal' : null;
  }

  get isTextArea(): boolean {
    return this.type === 'textarea';
  }

  get canClear(): boolean {
    const currentValue = this.control.value;
    return (
      (this.type === 'text' || this.type === 'search') &&
      !this.isDisabled &&
      !this.readonly &&
      currentValue !== null &&
      `${currentValue}`.length > 0
    );
  }

  get hasRightAdornment(): boolean {
    return this.canClear || this.type === 'password' || this.type === 'email' || !!this.suffixIcon;
  }

  get inputClassMap(): Record<string, boolean> {
    return {
      'h-[46px] py-2.5 placeholder:text-[14px]': this.size === 'lg',
      'h-10 py-2 placeholder:text-[14px]': this.size === 'md',
      'h-9 py-1.5 placeholder:text-[14px]': this.size === 'sm',
      '!text-sm text-slate900': this.size === 'lg',
      '!text-[14px] text-slate900': this.size === 'md',
      '!text-[13px] text-slate900': this.size === 'sm',
      'pl-3': !this.currencyOnLeft,
      'pl-7': this.currencyOnLeft,
      'pr-3': !this.hasRightAdornment,
      'pr-8': this.hasRightAdornment && this.size === 'sm',
      'pr-9': this.hasRightAdornment && this.size === 'md',
      'pr-10': this.hasRightAdornment && this.size === 'lg',
    };
  }

  get textareaClassMap(): Record<string, boolean> {
    return {
      '!px-3 py-2.5': this.size === 'lg',
      '!px-3 py-2': this.size === 'md',
      '!px-3 py-1.5': this.size === 'sm',
      '!text-sm text-slate900': this.size === 'lg',
      '!text-[14px] text-slate900': this.size === 'md',
      '!text-[13px] text-slate900': this.size === 'sm',
    };
  }

  get adornmentIconSize(): number {
    return this.size === 'md' ? 20 : 20;
  }

  get shouldShowError(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }

  get shouldShowAnyError(): boolean {
    return !!this.externalErrorMessage || this.shouldShowError;
  }

  get resolvedErrorMessage(): string {
    return this.externalErrorMessage || this.firstErrorMessage;
  }

  get isSuccessState(): boolean {
    return this.control.valid && (this.control.touched || this.control.dirty);
  }

  get isDisabled(): boolean {
    return this.disabled || this.control.disabled;
  }

  get firstErrorMessage(): string {
    const errors = this.control.errors;
    if (!errors) {
      return '';
    }

    const serverError = this.resolveTextError(errors['server'] ?? errors['api']);
    if (serverError) {
      return serverError;
    }

    if (errors['required']) {
      return `${this.fieldName} is required.`;
    }

    if (errors['email']) {
      return 'Please enter a valid email address.';
    }

    if (errors['emailsSame']) {
      return 'Company email and Compliance email must not be the same.';
    }

    if (errors['url']) {
      return `${this.fieldName} must be a valid URL (e.g. https://example.com).`;
    }

    if (errors['companyNumber']) {
      return `${this.fieldName} must be 8 digits or 2 letters followed by 6 digits.`;
    }

    if (errors['fcaFirmReferenceNumber']) {
      return `${this.fieldName} must be 6 to 10 digits.`;
    }

    if (errors['address']) {
      return `${this.fieldName} is required and cannot exceed 255 characters.`;
    }

    if (errors['city']) {
      return `${this.fieldName} must contain only letters (max 100 characters, no spaces).`;
    }

    if (errors['companyNameOrTitle']) {
      return `${this.fieldName} is required and cannot exceed 100 characters.`;
    }

    if (errors['password']) {
      return 'Password must be 8+ characters with uppercase, lowercase, number, and special character.';
    }

    if (errors['passwordMismatch']) {
      return 'Passwords do not match.';
    }

    if (errors['mismatch']) {
      return 'Values do not match.';
    }

    if (errors['postcode']) {
      return 'Please enter a valid UK postcode (e.g. SW1A 1AA).';
    }

    if (errors['phone']) {
      return `${this.fieldName} must be a valid phone number (10-15 digits).`;
    }

    if (errors['telephone']) {
      return `${this.fieldName} must be a valid telephone number (8-15 digits).`;
    }

    if (errors['niNumber']) {
      return 'Please enter a valid NI number (e.g. AB123456C).';
    }

    if (errors['range']) {
      return `${this.fieldName} must be between ${errors['range'].min} and ${errors['range'].max}.`;
    }

    if (errors['termOrToAge']) {
      return 'Enter either a Term or a To Age.';
    }

    if (errors['termOrToAgeBoth']) {
      return 'Enter only one of Term or To Age, not both.';
    }

    if (errors['toAgeNotAllowed']) {
      return 'To Age is not allowed when both lives are assured — enter a Term instead.';
    }

    if (errors['toAgeRange']) {
      return `${this.fieldName} must be between ${errors['toAgeRange'].min} and ${errors['toAgeRange'].max} (greater than age next birthday).`;
    }

    if (errors['ipTermRange']) {
      return `${this.fieldName} must be between ${errors['ipTermRange'].min} and ${errors['ipTermRange'].max}.`;
    }

    if (errors['ipToAgeRange']) {
      return `${this.fieldName} must be between ${errors['ipToAgeRange'].min} and ${errors['ipToAgeRange'].max} (at least age next birthday + 5).`;
    }

    if (errors['monthlyRange']) {
      return `${this.fieldName} must be between ${errors['monthlyRange'].min} and ${errors['monthlyRange'].max}.`;
    }

    if (errors['hasNumbers']) {
      return `${this.fieldName} cannot contain numbers.`;
    }

    if (errors['lettersOnly']) {
      return `${this.fieldName} can include letters, spaces, and special characters only (no numbers, max 50).`;
    }
    if (errors['fullName']) {
      return `${this.fieldName} can include letters, spaces, and special characters only (no numbers, max 100).`;
    }
    if (errors['minAge']) {
      return `${this.fieldName} must be at least ${errors['minAge'].required} years old.`;
    }

    if (errors['futureDate'] || errors['notFutureDate']) {
      return `${this.fieldName} cannot be a future date.`;
    }

    if (errors['pastDate'] || errors['notPastDate']) {
      return `${this.fieldName} cannot be a past date.`;
    }

    if (errors['requiredBoolean']) {
      return `${this.fieldName} is required.`;
    }

    if (errors['minlength']) {
      return `Minimum ${errors['minlength'].requiredLength} characters required.`;
    }

    if (errors['maxlength']) {
      return `Maximum ${errors['maxlength'].requiredLength} characters allowed.`;
    }

    if (errors['min']) {
      return `${this.fieldName} must be at least ${errors['min'].min}.`;
    }

    if (errors['max']) {
      return `${this.fieldName} must not exceed ${errors['max'].max}.`;
    }

    if (errors['pattern']) {
      return 'Please enter a valid value.';
    }

    if (errors['fileType'] || errors['documentFileType']) {
      return 'Accepted formats: PDF, PNG, JPG, JPEG.';
    }

    if (errors['fileSize']) {
      return `File size must be ${errors['fileSize'].maxSizeMB}MB or less.`;
    }

    if (errors['dobFormat']) {
      return 'Date of birth must be in dd-mm-yyyy format.';
    }

    if (errors['dobPastDate']) {
      return 'Date of birth must be a valid past date.';
    }

    if (errors['accountNumber']) {
      return 'Please enter a valid account number (8 digits).';
    }

    if (errors['sortCode']) {
      return 'Please enter a valid sort code (6 digits).';
    }

    if (errors['dobMinAge']) {
      return `Minimum age is ${errors['dobMinAge'].required} years.`;
    }

    if (errors['name']) {
      return `Please enter a valid name.`;
    }

    if (errors['description']) {
      return `Description can contain letters, numbers, and special characters (max 1000 words).`;
    }

    return 'Invalid input.';
  }

  get fieldName(): string {
    return this.errorLabel || this.label || 'This field';
  }

  get resolvedId(): string {
    return this.id || this.generatedId;
  }

  clear(): void {
    if (this.isDisabled || this.readonly) {
      return;
    }

    this.control.setValue('');
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
  }

  togglePasswordVisibility(): void {
    if (this.type !== 'password') {
      return;
    }

    this.showPassword = !this.showPassword;
  }

handleInput(event: Event): void {
  const inputElement = event.target as HTMLInputElement;

  if (this.type !== 'number') {
    const transformed = this.applyTextCaseToValue(inputElement.value);

    if (transformed !== inputElement.value) {
      inputElement.value = transformed;
      this.control.setValue(transformed);
    }

    return;
  }

  let sanitized = inputElement.value.replace(/[^0-9.-]/g, '');

  // Remove negative sign when field doesn't allow negatives. By default (no
  // `allowNegative` and no negative `min`), negatives are NOT allowed.
  if (!this.negativeAllowed) {
    sanitized = sanitized.replace(/-/g, '');
  } else {
    // Allow only one '-' and only at the beginning
    sanitized = sanitized.replace(/(?!^)-/g, '');
  }

  // Allow only one decimal point
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }

  if (sanitized !== inputElement.value) {
    inputElement.value = sanitized;
    this.control.setValue(sanitized);
  }
}

  /** Block characters that are valid in a native number input but unwanted
   *  here: the exponent 'e'/'E', and '+'. '-' is blocked for non-negative fields. */
  handleKeydown(event: KeyboardEvent): void {
    if (this.type !== 'number') return;

    const key = event.key;
    const input = event.target as HTMLInputElement;

    if (key === 'e' || key === 'E' || key === '+') {
      event.preventDefault();
      return;
    }

    if (this.decimalPlaces === 0 && (key === '.' || key === ',')) {
      event.preventDefault();
      return;
    }

    if (key === '-') {
      if (!this.negativeAllowed || input.selectionStart !== 0 || input.value.includes('-')) {
        event.preventDefault();
      }
    }
  }

  /**
   * Negatives are opt-in: either explicitly via `allowNegative`, or implicitly by
   * declaring a negative `min` — the latter is how callers did it before the flag
   * existed, so it keeps working.
   */
  private get negativeAllowed(): boolean {
    return this.allowNegative || (this.min !== null && this.min < 0);
  }

  handleFocus(event: Event): void {
    if (this.type !== 'number') return;
    const input = event.target as HTMLInputElement;
    const val = String(this.control.value ?? '').trim();
    if (val === '0') {
      this.control.setValue('');
      input.value = '';
    }
  }

  handleBlur(event: Event): void {
    if (this.type !== 'number') return;
    const input = event.target as HTMLInputElement;
    const val = String(this.control.value ?? '').trim();
    if (val === '') {
      // leave empty (we treat empty as undefined on serializer)
      this.control.setValue('');
      input.value = '';
      return;
    }
    // sanitize to numeric and remove leading zeros
    let n = parseFloat(val.replace(/,/g, ''));
    if (isNaN(n)) {
      this.control.setValue('');
      input.value = '';
    } else {
      // Clamp to min/max when specified
      if (this.min !== null && n < this.min) n = this.min;
      if (this.max !== null && n > this.max) n = this.max;
      const formatted = n.toFixed(this.decimalPlaces);
      const displayValue = this.decimalPlaces === 0 ? formatted : String(parseFloat(formatted));
      this.control.setValue(this.decimalPlaces === 0 ? parseInt(formatted, 10) : parseFloat(formatted));
      input.value = displayValue;
    }
  }

  private applyTextCaseToValue(value: string): string {
    if (this.textCase === 'uppercase') {
      return value.toUpperCase();
    }

    if (this.textCase === 'lowercase') {
      return value.toLowerCase();
    }

    if (this.textCase === 'capitalize') {
      if (!value) return value;
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    if (this.textCase === 'titlecase') {
      return value
        .split(/(\s+)/)
        .map((part) => (/^\s+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
        .join('');
    }

    return value;
  }

  handlePaste(event: ClipboardEvent): void {
    if (!this.preventPaste) {
      return;
    }

    event.preventDefault();
  }

  handleCopy(event: ClipboardEvent): void {
    if (!this.preventCopy) {
      return;
    }

    event.preventDefault();
  }

  private syncDisabledState(): void {
    if (this.disabled && this.control.enabled) {
      this.control.disable({ emitEvent: false });
      this.disabledByInput = true;
    }

    if (!this.disabled && this.disabledByInput && this.control.disabled) {
      this.control.enable({ emitEvent: false });
      this.disabledByInput = false;
    }
  }

  private applyInputSpecificValidation(): void {
    const validators = this.baseValidator ? [this.baseValidator] : [];

    if (this.required) {
      validators.push(CustomValidators.required);
    }

    if (this.type === 'email') {
      validators.push(CustomValidators.email);
    }

    if (this.type === 'url') {
      validators.push(CustomValidators.url);
    }

    if (this.type === 'tel') {
      validators.push(CustomValidators.phone);
    }

    if (this.type === 'password' && this.enforcePasswordStrength) {
      validators.push(CustomValidators.password);
    }

    this.control.setValidators(validators);
    this.control.setAsyncValidators(this.baseAsyncValidator);
    this.control.updateValueAndValidity({ emitEvent: false });
  }

  private captureBaseValidators(): void {
    this.baseValidator = this.control.validator;
    this.baseAsyncValidator = this.control.asyncValidator;
  }

  private resolveTextError(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  get currentCount(): number {
  return String(this.control?.value ?? '').length;
}
}
